import { z } from 'zod';

import { isoDatetimeToDate } from '../codecs';
import { PlayerId } from '../roster';

export type AssignmentId = Assignment['id'];
export type TrackId = string;

export type Assignment = z.output<typeof assignmentSchema>;
export type AssignmentDTO = z.input<typeof assignmentSchema>;

export const assignmentSchema = z.object({
  conflicts: z.array(z.string()),
  createdAt: isoDatetimeToDate,
  duration: z.number(),
  hasChildren: z.boolean(),
  id: z.uuid(),
  ignore: z.boolean(),
  imageUrl: z.string(),
  parent: z.uuid().optional(),
  playerId: z.uuid().nullable(),
  sessionId: z.uuid(),
  shots: z.number(),
  suggestedPlayerId: z.uuid().optional(),
  trackId: z.string(),
  updatedAt: isoDatetimeToDate,
});

export interface AssignmentWithDuration extends Assignment {
  duration: number;
}

export type Assigned<T extends Assignment> = Omit<T, 'ignore' | 'playerId'> & {
  ignore: false;
  playerId: PlayerId;
};
export type Ignored<T extends Assignment> = Omit<T, 'ignore'> & {
  ignore: true;
};
export type Unassigned<T extends Assignment> = Omit<T, 'ignore' | 'playerId'> & {
  ignore: false;
  playerId: null;
};
export type UnassignedWithSuggestion<T extends Assignment> = Unassigned<T> & {
  suggestedPlayerId: PlayerId;
};

export const AssignmentUtils = {
  hasPrediction(assignment: Assignment): boolean {
    return typeof assignment.suggestedPlayerId !== 'undefined';
  },
  isAssigned(assignment: Assignment): assignment is Assigned<Assignment> {
    return !assignment.ignore && assignment.playerId !== null;
  },
  isAssignedWithDuration(
    assignment: AssignmentWithDuration,
  ): assignment is Assigned<AssignmentWithDuration> {
    return !assignment.ignore && assignment.playerId !== null;
  },
  isIgnored(assignment: Assignment): assignment is Ignored<Assignment> {
    return assignment.ignore;
  },
  isIgnoredWithDuration(
    assignment: AssignmentWithDuration,
  ): assignment is Ignored<AssignmentWithDuration> {
    return assignment.ignore;
  },
  isUnassigned(assignment: Assignment): assignment is Unassigned<Assignment> {
    return !assignment.ignore && assignment.playerId === null;
  },
  isUnassignedWithDuration(
    assignment: AssignmentWithDuration,
  ): assignment is Unassigned<AssignmentWithDuration> {
    return !assignment.ignore && assignment.playerId === null;
  },
};
